/**
 * Edit a GENLOOKUP definition (header + slab/fixed rows) inline, without
 * leaving the calculation form. Auto-loads the saved definition when a
 * known name is picked; otherwise starts from a friendly "new lookup"
 * template. Persists to the backend with its own "Save changes" button.
 */
import {
  forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState,
} from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, FormControl, Grid,
  IconButton, InputLabel, MenuItem, Paper, Select, Stack, Switch, Table,
  TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import { LookupsApi } from './api';
import type {
  LookupDType, LookupDefinition, LookupRangeType, LookupRow,
} from './types';

const DTYPE_LABELS: Record<LookupDType, string> = { N: 'Number', D: 'Date', S: 'Text' };

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}(?:[T ].*)?$/;

/** Sniff a data type from a sample of typed cell strings. Numeric beats date
 *  beats text. Empty strings are ignored entirely. Falls back to Text. */
function inferDType(samples: Array<string | null | undefined>): LookupDType {
  const nonEmpty = samples
    .map((s) => (s ?? '').trim())
    .filter((s) => s.length > 0);
  if (nonEmpty.length === 0) return 'N'; // sensible default for empty draft
  const allNumeric = nonEmpty.every((s) => Number.isFinite(Number(s)));
  if (allNumeric) return 'N';
  const allDates = nonEmpty.every((s) => ISO_DATE_RE.test(s) && !Number.isNaN(Date.parse(s)));
  if (allDates) return 'D';
  return 'S';
}

/** Compare two raw cell strings honouring the key column's data type:
 *  Number -> numeric, Date -> chronological, Text -> locale. Empty or
 *  unparseable values sort to the end so half-filled draft rows don't jump
 *  to the top while the user is still typing them. */
function cmpKey(a: string | null | undefined, b: string | null | undefined, type: LookupDType): number {
  const sa = (a ?? '').toString().trim();
  const sb = (b ?? '').toString().trim();
  if (!sa && !sb) return 0;
  if (!sa) return 1;
  if (!sb) return -1;
  if (type === 'N') {
    const na = Number(sa); const nb = Number(sb);
    const fa = Number.isFinite(na); const fb = Number.isFinite(nb);
    if (fa && fb) return na - nb;
    if (fa !== fb) return fa ? -1 : 1; // unparseable numbers sort last
    return sa.localeCompare(sb);
  }
  if (type === 'D') {
    const da = Date.parse(sa); const db = Date.parse(sb);
    const fa = !Number.isNaN(da); const fb = !Number.isNaN(db);
    if (fa && fb) return da - db;
    if (fa !== fb) return fa ? -1 : 1;
    return sa.localeCompare(sb);
  }
  return sa.localeCompare(sb, undefined, { sensitivity: 'base' });
}

/** Sort rows ascending by From (min_key_value), tie-broken by To
 *  (max_key_value), per the key data type. Pure + stable (returns a new array). */
function sortRowsByRange<T extends LookupRow>(rows: T[], keyType: LookupDType): T[] {
  return rows
    .map((r, i) => [r, i] as const)
    .sort(([a, ia], [b, ib]) => {
      const c = cmpKey(a.min_key_value, b.min_key_value, keyType);
      if (c !== 0) return c;
      const c2 = cmpKey(a.max_key_value, b.max_key_value, keyType);
      if (c2 !== 0) return c2;
      return ia - ib; // preserve original order on a true tie
    })
    .map(([r]) => r);
}

interface Props {
  lookupName: string;
  /** When false, the per-row "Effective from / Valid until" columns are
   *  hidden because the calc isn't filtering the lookup by an as-of date —
   *  asking for those dates would be misleading. */
  showDateFilter?: boolean;
  /** Called after a successful save so the parent can refresh its list. */
  onSaved?: (def: LookupDefinition) => void;
}

/** Imperative handle exposed to the parent so it can flush pending edits
 *  (e.g. when the user clicks "Save & re-run" on the parent calc dialog).
 */
export interface LookupInlineEditorHandle {
  /** Returns true if there are unsaved edits. */
  isDirty: () => boolean;
  /** Persists pending edits if any. Throws on save failure so the parent
   *  can abort its own save. */
  flush: () => Promise<void>;
}

const EMPTY = (name: string): LookupDefinition => ({
  lookup_table: name,
  lookup_type: 'S',
  key_data_type: 'N',
  result_data_type: 'N',
  rows: [{ result_value: '', min_key_value: '', max_key_value: '' }],
});

// Rows are edited by array position and re-sorted on blur, so each needs a
// stable identity (not the array index) for its React key — otherwise focus
// would land on the wrong field when a row moves. ``_rid`` is UI-only and is
// stripped before the definition is persisted.
let _ridSeq = 0;
const nextRid = () => (_ridSeq += 1);
type EditRow = LookupRow & { _rid: number };
type EditDef = Omit<LookupDefinition, 'rows'> & { rows: EditRow[] };

const withIds = (d: LookupDefinition): EditDef => ({
  ...d, rows: d.rows.map((r) => ({ ...r, _rid: nextRid() })),
});
const stripIds = (d: EditDef): LookupDefinition => ({
  ...d,
  rows: d.rows.map((r) => {
    const rest: LookupRow & { _rid?: number } = { ...r };
    delete rest._rid;
    return rest;
  }),
});
// Load/refresh a definition into edit state, sorted by From/To per key type.
const sorted = (d: EditDef): EditDef => ({ ...d, rows: sortRowsByRange(d.rows, d.key_data_type) });

const LookupInlineEditor = forwardRef<LookupInlineEditorHandle, Props>(
  function LookupInlineEditor({ lookupName, showDateFilter = false, onSaved }, ref) {
  const [def, setDef] = useState<EditDef | null>(null);
  const [dirty, setDirty] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  // Refs that the imperative handle reads so it always sees the latest state.
  const defRef = useRef<EditDef | null>(null);
  const dirtyRef = useRef(false);
  defRef.current = def;
  dirtyRef.current = dirty;

  // Reload definition whenever the picked name changes.
  useEffect(() => {
    const name = (lookupName || '').trim();
    setError(null); setInfo(null); setDirty(false);
    if (!name) {
      setDef(null); setIsNew(false);
      return;
    }
    setLoading(true);
    LookupsApi.get(name)
      .then((d) => { setDef(sorted(withIds(d))); setIsNew(false); })
      .catch(() => {
        // Treat 404 as "this lookup doesn't exist yet -- start a draft".
        setDef(withIds(EMPTY(name.toUpperCase()))); setIsNew(true);
      })
      .finally(() => setLoading(false));
  }, [lookupName]);

  // Expose imperative API to the parent dialog.
  useImperativeHandle(ref, () => ({
    isDirty: () => dirtyRef.current,
    flush: async () => {
      if (!dirtyRef.current || !defRef.current) return;
      const saved = await LookupsApi.save(stripIds(defRef.current));
      setDef(sorted(withIds(saved)));
      setIsNew(false);
      setDirty(false);
      onSaved?.(saved);
    },
  }), [onSaved]);

  // Auto-derived types from typed row values. Declared BEFORE any early
  // return so hook order stays stable across renders (React error #310).
  // Falls back to a safe Number/Number default when ``def`` is null.
  const inferredKey = useMemo<LookupDType>(() => inferDType(
    (def?.rows ?? []).flatMap((r) => [r.min_key_value, r.max_key_value]),
  ), [def?.rows]);
  const inferredResult = useMemo<LookupDType>(() => inferDType(
    (def?.rows ?? []).map((r) => r.result_value),
  ), [def?.rows]);

  // Track whether the user has manually overridden the auto-detect. Default
  // path mirrors inferred-into-def so the persisted lookup stays in sync
  // without forcing the user to think about data types.
  const [overrideTypes, setOverrideTypes] = useState(false);
  useEffect(() => {
    if (overrideTypes || !def) return;
    if (def.key_data_type === inferredKey && def.result_data_type === inferredResult) return;
    setDef((prev) => prev && {
      ...prev,
      key_data_type: inferredKey,
      result_data_type: inferredResult,
    });
    // Passive sync (not a user edit) -- don't flip ``dirty`` here; whatever
    // row edit drove the re-inference already set it.
  }, [inferredKey, inferredResult, overrideTypes, def]);

  if (!lookupName?.trim()) return null;

  if (loading || !def) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="caption">Loading lookup…</Typography>
      </Box>
    );
  }

  const isSlab = def.lookup_type === 'S';

  const updateHeader = <K extends keyof LookupDefinition>(k: K, v: LookupDefinition[K]) => {
    setDef({ ...def, [k]: v });
    setDirty(true);
  };
  // Rows are addressed by stable _rid (not array index) so a re-sort triggered
  // on blur can't make a later edit/delete in the same interaction hit the
  // wrong row.
  const updateRow = (rid: number, patch: Partial<LookupRow>) => {
    setDef({ ...def, rows: def.rows.map((r) => (r._rid === rid ? { ...r, ...patch } : r)) });
    setDirty(true);
  };
  const addRow = () => {
    setDef({
      ...def,
      rows: [
        ...def.rows,
        { result_value: '', min_key_value: '', max_key_value: isSlab ? '' : null, _rid: nextRid() },
      ],
    });
    setDirty(true);
  };
  const removeRow = (rid: number) => {
    setDef({ ...def, rows: def.rows.filter((r) => r._rid !== rid) });
    setDirty(true);
  };

  // Effective key type drives the sort order: the user's manual override when
  // set, otherwise the type auto-detected from the From/To values.
  const effectiveKeyType: LookupDType = overrideTypes ? def.key_data_type : inferredKey;
  // Re-sort rows by From/To. Called when a From/To edit is committed (onBlur)
  // and when the key type changes, so the table stays ordered without
  // reordering rows mid-keystroke. Row identity (_rid keys) keeps focus put.
  const commitSort = () => {
    setDef((prev) => (prev
      ? { ...prev, rows: sortRowsByRange(prev.rows, overrideTypes ? prev.key_data_type : inferredKey) }
      : prev));
  };

  const onSave = async () => {
    setSaving(true); setError(null); setInfo(null);
    try {
      const saved = await LookupsApi.save(stripIds(def));
      setDef(sorted(withIds(saved)));
      setIsNew(false);
      setDirty(false);
      setInfo(`Saved · ${saved.rows.length} row(s).`);
      onSaved?.(saved);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  // Column widths: when the date columns are hidden, redistribute that space
  // to the key/range/result inputs so the table doesn't look lopsided.
  const colWidths = showDateFilter
    ? (isSlab
        ? { key: '20%', to: '20%', result: '24%', date: '15%' }
        : { key: '32%', result: '32%', date: '15%' })
    : (isSlab
        ? { key: '30%', to: '30%', result: '36%' }
        : { key: '46%', result: '50%' });

  const colSpan =
    1 /* key */ + (isSlab ? 1 : 0) + 1 /* result */ + (showDateFilter ? 2 : 0) + 1 /* delete */;

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 1.5, bgcolor: 'grey.50' }}>
      {/* Header: title + status chip + save action on a single tidy row. */}
      <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Lookup · {def.lookup_table}
        </Typography>
        {isNew && (
          <Chip
            size="small"
            label="New — not saved"
            color="warning"
            variant="outlined"
            sx={{ height: 22 }}
          />
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button
          size="small"
          variant={dirty ? 'contained' : 'outlined'}
          startIcon={<SaveIcon />}
          onClick={onSave}
          disabled={saving || !dirty}
        >
          {saving ? 'Saving…' : isNew ? 'Create lookup' : 'Save changes'}
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}
      {info  && <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setInfo(null)}>{info}</Alert>}

      {/* Match-by + detected types strip. Balanced 6/6 split so the two
          groups have equal visual weight. */}
      <Grid container spacing={2} alignItems="center" mb={1.5}>
        <Grid item xs={12} md={6}>
          <FormControl size="small" fullWidth>
            <InputLabel>Match by</InputLabel>
            <Select
              label="Match by" value={def.lookup_type}
              onChange={(e) => updateHeader('lookup_type', e.target.value as LookupRangeType)}
            >
              <MenuItem value="S">Range — value falls between From and To</MenuItem>
              <MenuItem value="F">Exact — value equals Key value</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" flexWrap="wrap">
            <AutoFixHighIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Detected:
            </Typography>
            <Tooltip title="Inferred from the From / To column values">
              <Chip
                size="small"
                variant="outlined"
                label={`Key · ${DTYPE_LABELS[overrideTypes ? def.key_data_type : inferredKey]}`}
                color={overrideTypes ? 'default' : 'primary'}
              />
            </Tooltip>
            <Tooltip title="Inferred from the Result column values">
              <Chip
                size="small"
                variant="outlined"
                label={`Result · ${DTYPE_LABELS[overrideTypes ? def.result_data_type : inferredResult]}`}
                color={overrideTypes ? 'default' : 'primary'}
              />
            </Tooltip>
            <Tooltip title="Manually override the detected types (rarely needed)">
              <Stack direction="row" alignItems="center" spacing={0.25} sx={{ ml: 0.5 }}>
                <Switch
                  size="small"
                  checked={overrideTypes}
                  onChange={(e) => setOverrideTypes(e.target.checked)}
                />
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Override
                </Typography>
              </Stack>
            </Tooltip>
          </Stack>
          {overrideTypes && (
            <Stack direction="row" spacing={1} mt={1} justifyContent="flex-end">
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Key type</InputLabel>
                <Select
                  label="Key type" value={def.key_data_type}
                  onChange={(e) => {
                    // Re-sort with the newly chosen type (text vs number vs
                    // date order differs) in the same update.
                    const v = e.target.value as LookupDType;
                    setDef({ ...def, key_data_type: v, rows: sortRowsByRange(def.rows, v) });
                    setDirty(true);
                  }}
                >
                  <MenuItem value="N">{DTYPE_LABELS.N}</MenuItem>
                  <MenuItem value="D">{DTYPE_LABELS.D}</MenuItem>
                  <MenuItem value="S">{DTYPE_LABELS.S}</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Result type</InputLabel>
                <Select
                  label="Result type" value={def.result_data_type}
                  onChange={(e) => updateHeader('result_data_type', e.target.value as LookupDType)}
                >
                  <MenuItem value="N">{DTYPE_LABELS.N}</MenuItem>
                  <MenuItem value="D">{DTYPE_LABELS.D}</MenuItem>
                  <MenuItem value="S">{DTYPE_LABELS.S}</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          )}
        </Grid>
      </Grid>

      <Divider sx={{ mb: 1.5 }} />

      {/* Rows section: caption on the left, add-row affordance on the right. */}
      <Stack direction="row" alignItems="center" mb={1}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {isSlab
            ? `Define a range per row. The first range that contains the key value wins. Rows stay sorted by From (${DTYPE_LABELS[effectiveKeyType].toLowerCase()} order).`
            : `Define one exact match per row. Rows stay sorted by Key value (${DTYPE_LABELS[effectiveKeyType].toLowerCase()} order).`}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" startIcon={<AddIcon fontSize="small" />} onClick={addRow}>
          Add row
        </Button>
      </Stack>

      <Table size="small" sx={{
        '& th': { fontWeight: 600, color: 'text.secondary', fontSize: 13, py: 0.75 },
        '& td': { py: 0.5 },
      }}>
        <TableHead>
          <TableRow>
            <TableCell width={colWidths.key}>{isSlab ? 'From' : 'Key value'}</TableCell>
            {isSlab && <TableCell width={colWidths.to}>To</TableCell>}
            <TableCell width={colWidths.result}>Result value</TableCell>
            {showDateFilter && <TableCell width={colWidths.date}>Effective from</TableCell>}
            {showDateFilter && <TableCell width={colWidths.date}>Valid until</TableCell>}
            <TableCell width={40} />
          </TableRow>
        </TableHead>
        <TableBody>
          {def.rows.map((r) => (
            <TableRow key={r._rid}>
              <TableCell>
                <TextField size="small" fullWidth value={r.min_key_value}
                  onChange={(e) => updateRow(r._rid, { min_key_value: e.target.value })}
                  onBlur={commitSort} />
              </TableCell>
              {isSlab && (
                <TableCell>
                  <TextField size="small" fullWidth value={r.max_key_value ?? ''}
                    onChange={(e) => updateRow(r._rid, { max_key_value: e.target.value })}
                    onBlur={commitSort} />
                </TableCell>
              )}
              <TableCell>
                <TextField size="small" fullWidth value={r.result_value}
                  onChange={(e) => updateRow(r._rid, { result_value: e.target.value })} />
              </TableCell>
              {showDateFilter && (
                <TableCell>
                  <TextField size="small" type="date" fullWidth value={r.eff_from ?? ''}
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => updateRow(r._rid, { eff_from: e.target.value || null })} />
                </TableCell>
              )}
              {showDateFilter && (
                <TableCell>
                  <TextField size="small" type="date" fullWidth value={r.valid_upto ?? ''}
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => updateRow(r._rid, { valid_upto: e.target.value || null })} />
                </TableCell>
              )}
              <TableCell align="right">
                <IconButton size="small" onClick={() => removeRow(r._rid)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          {def.rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={colSpan} align="center" sx={{ color: 'text.secondary', py: 2 }}>
                No rows yet. Click <strong>Add row</strong> to start.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  );
});

export default LookupInlineEditor;
